import React, { useState, useEffect } from 'react';
import { ArrowDownUp, TrendingUp, TrendingDown, Clock, DollarSign, Euro, PoundSterling, Banknote } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vaiu/ui/tabs';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Badge } from '@vaiu/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { unifiedTokenService } from '@/services/UnifiedTokenService';
import type { MarketData, Order, TokenTransfer } from '@/types/token';
import DevelopmentNotice from '@/components/DevelopmentNotice';

const MAJOR_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', icon: DollarSign },
  { code: 'EUR', name: 'Euro', icon: Euro },
  { code: 'GBP', name: 'British Pound', icon: PoundSterling },
  { code: 'JPY', name: 'Japanese Yen', icon: Banknote },
  { code: 'CAD', name: 'Canadian Dollar', icon: DollarSign },
  { code: 'AUD', name: 'Australian Dollar', icon: DollarSign },
  { code: 'CHF', name: 'Swiss Franc', icon: Banknote },
  { code: 'CNY', name: 'Chinese Yuan', icon: Banknote }
];

export default function CurrencyMarketPage() {
  const { toast } = useToast();
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState('100');
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [recentTransfers, setRecentTransfers] = useState<TokenTransfer[]>([]);
  const [openOrders, setOpenOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [limitPrice, setLimitPrice] = useState('');

  useEffect(() => {
    loadMarketData();
    loadOrders();
    const interval = setInterval(loadMarketData, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (fromCurrency && toCurrency && fromCurrency !== toCurrency) {
      loadExchangeRate();
    }
  }, [fromCurrency, toCurrency]);

  const loadMarketData = async () => {
    try {
      const symbols = MAJOR_CURRENCIES
        .filter(c => c.code !== 'USD')
        .map(c => `USD_${c.code}`);
      const data = await unifiedTokenService.getMarketPrices(symbols);
      setMarketData(data);
    } catch (error) {
      console.error('Failed to load market data:', error);
    }
  };

  const loadExchangeRate = async () => {
    try {
      const rate = await unifiedTokenService.getExchangeRate(fromCurrency, toCurrency);
      setExchangeRate(rate);
    } catch (error) {
      console.error('Failed to load exchange rate:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const orders = await unifiedTokenService.getOrders({
        status: 'open',
        type: 'exchange'
      });
      setOpenOrders(orders);
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const handleExchange = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount to exchange.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      if (orderType === 'market') {
        // Direct exchange at market rate
        const transfer = await unifiedTokenService.exchangeCurrency(
          fromCurrency,
          toCurrency,
          parseFloat(amount)
        );
        
        toast({
          title: 'Exchange Successful',
          description: `Exchanged ${amount} ${fromCurrency} to ${transfer.metadata?.receivedAmount || (parseFloat(amount) * (exchangeRate || 1)).toFixed(2)} ${toCurrency}`
        });
        
        setRecentTransfers([transfer, ...recentTransfers.slice(0, 4)]);
      } else {
        // Place limit order
        const order = await unifiedTokenService.placeOrder({
          tokenId: `${fromCurrency}_${toCurrency}`,
          side: 'sell',
          amount: parseFloat(amount),
          orderType: 'limit',
          price: parseFloat(limitPrice),
          timeInForce: 'GTC'
        });
        
        toast({
          title: 'Order Placed',
          description: `Limit order to exchange ${amount} ${fromCurrency} at rate ${limitPrice}`
        });
        
        loadOrders();
      }
      
      setAmount('');
      setLimitPrice('');
    } catch (error: any) {
      toast({
        title: 'Exchange Failed',
        description: error.message || 'Failed to complete exchange',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await unifiedTokenService.cancelOrder(orderId);
      toast({
        title: 'Order Cancelled',
        description: 'Your order has been cancelled successfully.'
      });
      loadOrders();
    } catch (error: any) {
      toast({
        title: 'Cancellation Failed',
        description: error.message || 'Failed to cancel order',
        variant: 'destructive'
      });
    }
  };

  const estimatedAmount = amount && exchangeRate 
    ? (parseFloat(amount) * exchangeRate).toFixed(2)
    : '0.00';

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <DevelopmentNotice feature="Currency Market" />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Currency Market</h1>
        <p className="text-muted-foreground">
          Exchange currencies at competitive rates with real-time market pricing
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Exchange Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Exchange Currencies</CardTitle>
              <CardDescription>
                Convert between major world currencies instantly
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={orderType} onValueChange={(v) => setOrderType(v as 'market' | 'limit')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="market">Market Order</TabsTrigger>
                  <TabsTrigger value="limit">Limit Order</TabsTrigger>
                </TabsList>

                <TabsContent value="market" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Currency</Label>
                      <Select value={fromCurrency} onValueChange={setFromCurrency}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MAJOR_CURRENCIES.map(currency => (
                            <SelectItem key={currency.code} value={currency.code}>
                              <div className="flex items-center space-x-2">
                                <currency.icon className="w-4 h-4" />
                                <span>{currency.code} - {currency.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>To Currency</Label>
                      <Select value={toCurrency} onValueChange={setToCurrency}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MAJOR_CURRENCIES.map(currency => (
                            <SelectItem key={currency.code} value={currency.code}>
                              <div className="flex items-center space-x-2">
                                <currency.icon className="w-4 h-4" />
                                <span>{currency.code} - {currency.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSwapCurrencies}
                      className="rounded-full"
                    >
                      <ArrowDownUp className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount to Exchange</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  {exchangeRate && (
                    <Alert>
                      <AlertDescription>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Exchange Rate:</span>
                            <span className="font-medium">
                              1 {fromCurrency} = {exchangeRate.toFixed(4)} {toCurrency}
                            </span>
                          </div>
                          <div className="flex justify-between text-lg font-semibold">
                            <span>You will receive:</span>
                            <span>{estimatedAmount} {toCurrency}</span>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </TabsContent>

                <TabsContent value="limit" className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      Place a limit order to exchange at your desired rate. The order will execute when the market reaches your price.
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Currency</Label>
                      <Select value={fromCurrency} onValueChange={setFromCurrency}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MAJOR_CURRENCIES.map(currency => (
                            <SelectItem key={currency.code} value={currency.code}>
                              <div className="flex items-center space-x-2">
                                <currency.icon className="w-4 h-4" />
                                <span>{currency.code}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>To Currency</Label>
                      <Select value={toCurrency} onValueChange={setToCurrency}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MAJOR_CURRENCIES.map(currency => (
                            <SelectItem key={currency.code} value={currency.code}>
                              <div className="flex items-center space-x-2">
                                <currency.icon className="w-4 h-4" />
                                <span>{currency.code}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        step="0.01"
                        min="0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Limit Price (Rate)</Label>
                      <Input
                        type="number"
                        placeholder={exchangeRate?.toFixed(4) || '0.0000'}
                        value={limitPrice}
                        onChange={(e) => setLimitPrice(e.target.value)}
                        step="0.0001"
                        min="0"
                      />
                    </div>
                  </div>

                  {limitPrice && amount && (
                    <Alert>
                      <AlertDescription>
                        You will receive {(parseFloat(amount) * parseFloat(limitPrice)).toFixed(2)} {toCurrency} when executed
                      </AlertDescription>
                    </Alert>
                  )}
                </TabsContent>
              </Tabs>

              <Button 
                onClick={handleExchange} 
                disabled={loading || !amount || (orderType === 'limit' && !limitPrice)}
                className="w-full"
                size="lg"
              >
                {loading ? 'Processing...' : orderType === 'market' ? 'Exchange Now' : 'Place Limit Order'}
              </Button>
            </CardContent>
          </Card>

          {/* Open Orders */}
          {openOrders.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Open Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {openOrders.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">
                          {order.side === 'buy' ? 'Buy' : 'Sell'} {order.amount} @ {order.price}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {order.tokenId} • {new Date(order.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelOrder(order.id)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Market Rates Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Market Rates</CardTitle>
              <CardDescription>Live currency exchange rates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {marketData.map(data => {
                  const isPositive = data.changePercent24h >= 0;
                  return (
                    <div key={data.symbol} className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{data.symbol.replace('_', '/')}</div>
                        <div className="text-sm text-muted-foreground">
                          {data.price.toFixed(4)}
                        </div>
                      </div>
                      <Badge variant={isPositive ? 'default' : 'destructive'}>
                        {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(data.changePercent24h).toFixed(2)}%
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recent Transfers */}
          {recentTransfers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Exchanges</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentTransfers.map(transfer => (
                    <div key={transfer.id} className="flex items-center space-x-3">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {transfer.amount} {transfer.from} → {transfer.to}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(transfer.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                      <Badge variant={transfer.status === 'completed' ? 'default' : 'secondary'}>
                        {transfer.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
